<template>
  <!-- End-of-tour card: a small "scene" showing the thing the user just made,
  in the wild (a Slack unfurl, a DM thread, a leaderboard…), above the usual
  kicker / title / CTA. Rendered inside TourOverlay's card shell for steps
  with variant: 'completion'. Scenes live in ./scenes and receive the tour
  context so they can show real names and thumbnails where we have them. -->
  <div class="flex flex-col">
    <!-- Scene stage -->
    <div
      class="completion-stage px-5 pt-5 pb-4 border-b border-border-normal"
      :class="`completion-stage--${accent}`"
    >
      <div class="completion-window rounded-xl bg-white overflow-hidden">
        <component
          :is="sceneComponent"
          v-if="sceneComponent"
          :context="context"
        />
      </div>
    </div>

    <!-- Copy + CTA -->
    <div class="flex flex-col items-center text-center px-6 pt-4 pb-5">
      <div
        v-if="pill"
        class="completion-pill flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M4.5 10.5L8 14L15.5 6.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <BaseText
          type="label"
          size="xs"
        >
          {{ pill }}
        </BaseText>
      </div>
      <BaseText
        v-if="kicker"
        type="heading"
        size="overline"
        class="uppercase text-text-subdued"
        :class="pill ? 'mt-3' : ''"
      >
        {{ kicker }}
      </BaseText>
      <BaseText
        type="heading"
        size="md"
        class="text-text-loud"
        :class="kicker ? 'mt-1.5' : 'mt-3'"
      >
        {{ title }}
      </BaseText>
      <BaseText
        v-for="(paragraph, index) in paragraphs"
        :key="`completion-p-${index}`"
        type="body"
        size="sm"
        class="text-text-muted"
        :class="index === 0 ? 'mt-2' : 'mt-1.5'"
      >
        {{ paragraph }}
      </BaseText>
      <BaseButton
        primary
        class="w-40 mt-5"
        :forced-theme="accent"
        @click="$emit('continue')"
      >
        {{ ctaLabel }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import AdRankScene from './scenes/AdRankScene.vue'
import AdSavedScene from './scenes/AdSavedScene.vue'
import BoardSharedScene from './scenes/BoardSharedScene.vue'
import BrandTrackedScene from './scenes/BrandTrackedScene.vue'
import DiscoverySearchScene from './scenes/DiscoverySearchScene.vue'
import LensConnectedScene from './scenes/LensConnectedScene.vue'
import LensReportScene from './scenes/LensReportScene.vue'

// `scene` keys used in tourContent.js
const SCENES = {
  'ad-rank': AdRankScene,
  'ad-saved': AdSavedScene,
  'board-shared': BoardSharedScene,
  'brand-tracked': BrandTrackedScene,
  'discovery-search': DiscoverySearchScene,
  'lens-connected': LensConnectedScene,
  'lens-report': LensReportScene
}

export default {
  name: 'TourCompletionCard',
  props: {
    title: {
      type: String,
      required: true
    },
    paragraphs: {
      type: Array,
      default: () => []
    },
    kicker: {
      type: String,
      default: ''
    },
    ctaLabel: {
      type: String,
      default: 'Continue'
    },
    // Key in SCENES
    scene: {
      type: String,
      default: ''
    },
    // Green status pill text ("Link copied", "Brand tracked"…)
    pill: {
      type: String,
      default: ''
    },
    // Stage tint and button theme: 'blue' (library), 'red' (Spyder),
    // 'purple' (Discovery), 'black' (Lens), 'green' (Briefs)
    accent: {
      type: String,
      default: 'blue'
    },
    // Tour context: brandName, brandAvatar, boardName, adThumbnails, keyword…
    context: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    sceneComponent () {
      return SCENES[this.scene] || null
    }
  }
}
</script>

<style scoped>
.completion-stage {
  background-color: theme('colors.neutral.25');
}
.completion-stage--blue {
  background-image: radial-gradient(120% 80% at 50% 0%, rgba(31, 105, 255, 0.10) 0%, rgba(31, 105, 255, 0) 70%);
}
.completion-stage--red {
  background-image: radial-gradient(120% 80% at 50% 0%, rgba(237, 97, 90, 0.10) 0%, rgba(237, 97, 90, 0) 70%);
}
.completion-stage--purple {
  background-image: radial-gradient(120% 80% at 50% 0%, rgba(101, 52, 223, 0.12) 0%, rgba(101, 52, 223, 0) 70%);
}
.completion-stage--black {
  background-image: radial-gradient(120% 80% at 50% 0%, rgba(6, 7, 16, 0.10) 0%, rgba(6, 7, 16, 0) 70%);
}
.completion-stage--green {
  background-image: radial-gradient(120% 80% at 50% 0%, rgba(0, 168, 120, 0.12) 0%, rgba(0, 168, 120, 0) 70%);
}
.completion-window {
  box-shadow:
    0 0 0 1px rgba(24, 48, 123, 0.06),
    0 10px 24px -8px rgba(26, 48, 84, 0.18);
  animation: completion-in 420ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: 120ms;
}
.completion-pill {
  color: theme('colors.primary.green-100');
  background-color: rgba(0, 168, 120, 0.10);
}
@keyframes completion-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .completion-window {
    animation: none;
  }
}
</style>
